import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus, REQUEST_STATUSES } from 'commons/types/common';
import type { RootState } from 'stores/store';
import { HubspotToken } from 'commons/types/doctorsTypes';
import { Service } from 'commons/types/servicesTypes';
import {
  confirmMailOtp,
  confirmPhoneOtp,
  fetchUser,
  requestMailOtp,
  requestPhoneOtp,
  updateUser,
  getHubspotUser,
  updateUserCycle,
  setUserTimer,
  fetchServices,
} from './userClient';
import type { User } from './userTypes';

export interface UserState {
  user?: User;
  hubspotToken?: HubspotToken;
  userRequestStatus?: RequestStatus;
  userUpdateRequestStatus?: RequestStatus;
  otpRequestStatus?: RequestStatus;
  confirmOtpRequestStatus?: RequestStatus;
  hubspotRequestStatus?: RequestStatus;
  throttleError?: string;
  conflictError?: boolean;
  services?: Service[];
  servicesRequestStatus?: RequestStatus;
}

const initialState: UserState = {
  throttleError: '',
  conflictError: false,
};

export const getUserAsync = createAsyncThunk('user/get', () => fetchUser());
export const updateUserAsync = createAsyncThunk('user/update', (user: User) =>
  updateUser(user),
);
export const updateUserCycleAsync = createAsyncThunk(
  'user/updateCycle',
  ({ patientID, state }: { patientID: string; state: string }) =>
    updateUserCycle(patientID, state),
);

export const setUserTimerAsync = createAsyncThunk(
  'user/timer',
  (patientID: string) => setUserTimer(patientID),
);

export const getUserHubspotAsync = createAsyncThunk(
  'user/getHubspot',
  ({
    email,
    firstName,
    lastName,
  }: {
    email: string;
    firstName: string;
    lastName: string;
  }) => getHubspotUser(email, firstName, lastName),
);

export const requestMailOtpAsync = createAsyncThunk(
  'user/request-mail-otp',
  ({ email }: { email: string }) => requestMailOtp(email),
);

export const confirmMailOtpAsync = createAsyncThunk(
  'user/confirm-mail-otp',
  ({ email, otp }: { email: string; otp: string }) =>
    confirmMailOtp(email, otp),
);

export const requestPhoneOtpAsync = createAsyncThunk(
  'user/request-phone-otp',
  ({ phone }: { phone: string }) => requestPhoneOtp(phone),
);

export const confirmPhoneOtpAsync = createAsyncThunk(
  'user/confirm-phone-otp',
  ({ phone, otp }: { phone: string; otp: string }) =>
    confirmPhoneOtp(phone, otp),
);

export const getServicesAsync = createAsyncThunk('user/get-services', () =>
  fetchServices(),
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUser: (state) => {
      delete state.user;
      delete state.hubspotToken;
      delete state.userRequestStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserAsync.pending, (state) => {
        state.userRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUserAsync.fulfilled, (state, { payload }) => {
        state.userRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.user = payload;
      })
      .addCase(getUserAsync.rejected, (state) => {
        state.userRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(updateUserAsync.pending, (state, { meta }) => {
        state.user = meta.arg;
        state.userUpdateRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(updateUserAsync.fulfilled, (state) => {
        state.userUpdateRequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(updateUserAsync.rejected, (state) => {
        state.userUpdateRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(requestMailOtpAsync.pending, (state) => {
        state.otpRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(requestMailOtpAsync.fulfilled, (state, { payload }) => {
        if (payload?.status === 429) {
          state.otpRequestStatus = REQUEST_STATUSES.THROTTLED;
          state.throttleError = payload.availableIn;
        } else {
          state.otpRequestStatus = REQUEST_STATUSES.SUCCESS;
        }
      })
      .addCase(requestMailOtpAsync.rejected, (state) => {
        state.otpRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(confirmMailOtpAsync.pending, (state) => {
        state.confirmOtpRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(confirmMailOtpAsync.fulfilled, (state, { payload }) => {
        if (payload?.status === 409) {
          state.confirmOtpRequestStatus = REQUEST_STATUSES.ERROR;
          state.conflictError = true;
        } else {
          state.confirmOtpRequestStatus = REQUEST_STATUSES.SUCCESS;
          state.conflictError = false;
          state.user = payload;
        }
      })
      .addCase(confirmMailOtpAsync.rejected, (state) => {
        state.confirmOtpRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(requestPhoneOtpAsync.pending, (state) => {
        state.otpRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(requestPhoneOtpAsync.fulfilled, (state, { payload }) => {
        if (payload?.status === 429) {
          state.otpRequestStatus = REQUEST_STATUSES.THROTTLED;
          state.throttleError = payload.availableIn;
        } else {
          state.otpRequestStatus = REQUEST_STATUSES.SUCCESS;
        }
      })
      .addCase(requestPhoneOtpAsync.rejected, (state) => {
        state.otpRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(confirmPhoneOtpAsync.pending, (state) => {
        state.confirmOtpRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(confirmPhoneOtpAsync.fulfilled, (state, { payload }) => {
        if (payload?.status === 409) {
          state.confirmOtpRequestStatus = REQUEST_STATUSES.ERROR;
          state.conflictError = true;
        } else {
          state.confirmOtpRequestStatus = REQUEST_STATUSES.SUCCESS;
          state.conflictError = false;
          state.user = payload;
        }
      })
      .addCase(confirmPhoneOtpAsync.rejected, (state) => {
        state.confirmOtpRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getUserHubspotAsync.pending, (state) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUserHubspotAsync.fulfilled, (state, { payload }) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.hubspotToken = payload;
      })
      .addCase(getUserHubspotAsync.rejected, (state) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getServicesAsync.pending, (state) => {
        state.servicesRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getServicesAsync.fulfilled, (state, { payload }) => {
        state.servicesRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.services = payload;
      })
      .addCase(getServicesAsync.rejected, (state) => {
        state.servicesRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const { resetUser } = userSlice.actions;
export const getUser = (state: RootState) => state.user.user;
export const getHubspotToken = (state: RootState) => state.user.hubspotToken;
export const getUserRequestStatus = (state: RootState) =>
  state.user.userRequestStatus;
export const getUserUpdateRequestStatus = (state: RootState) =>
  state.user.userUpdateRequestStatus;
export const getOtpRequestStatus = (state: RootState) =>
  state.user.otpRequestStatus;
export const getConfirmOtpRequestStatus = (state: RootState) =>
  state.user.confirmOtpRequestStatus;
export const getThrottleError = (state: RootState) => state.user.throttleError;
export const getConflictError = (state: RootState) => state.user.conflictError;
export const getHubspotRequestStatus = (state: RootState) =>
  state.user.hubspotRequestStatus;
export const getServices = (state: RootState) => state.user.services;
export const getServicesRequestStatus = (state: RootState) =>
  state.user.servicesRequestStatus;

export default userSlice.reducer;
